import React from 'react';

import { ClientProfileFormValues, TUserProfileInfo } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServices } from 'hooks/useServices';
import {
  validateClientProfileFormValues,
  validateImmediatelyClientProfileFormValues,
  sanitizeClientProfileFormChange,
} from 'utils/clientProfileFormUtils';
import { useForm } from 'hooks/useForm';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import { normalizeUserProfileInfo } from 'services/production/utils/normalizeUserProfileInfo';
import { DONE_TIMEOUT_DURATION } from 'utils/constants';
import { useAsyncActionViewScreen } from 'hooks/useAsyncViewScreen';
import { useServiceState } from 'hooks/useServiceState';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { FormControlComponent } from 'design/formControl';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { ClientProfileInfoFormFieldset } from 'containers/ClientProfileFormFieldset';
import { CustomFieldsetComponent } from 'design/customFieldset/customFieldsetComponents';

type TProps = {
  clientId: number;
  userProfileInfo: TUserProfileInfo;
  onCancel(): void;
  onSubmitDidSucceed(): void;
};

export const ClientDetailsProfileEditor = (props: TProps) => {
  const { clientId, userProfileInfo, onCancel, onSubmitDidSucceed } = props;

  const intl = useIntlDictionary();

  const { updateClientInfo: updateClientInfoService } = useServices();

  // Валидаторы формы
  const validator = React.useMemo(() => validateClientProfileFormValues(intl), [
    intl,
  ]);

  const immediateValidator = React.useMemo(
    () => validateImmediatelyClientProfileFormValues(intl),
    [intl]
  );

  const initialValues = React.useMemo<ClientProfileFormValues>(() => {
    return normalizeUserProfileInfo(
      userProfileInfo
    ).toClientProfileFormValues();
  }, [userProfileInfo]);

  // Состояние формы, функция для валидирования,
  // хэндлер изменений инпута
  const {
    values,
    validation,
    validate,
    onChange,
    setValues,
    setValidation,
  } = useForm({
    initialValues,
    validator,
    immediateValidator,
    sanitizer: sanitizeClientProfileFormChange,
  });

  const onLocationChange = React.useCallback(
    (nextValue: string) => {
      setValues((prev) => ({
        ...prev,
        location: nextValue,
      }));

      setValidation((prev) => ({
        ...prev,
        location: undefined,
      }));
    },
    [setValidation, setValues]
  );

  // Состояние запроса на обновление профиля,
  // функция отправки запроса
  const [updateProfileState, updateProfile] = useServiceState(
    updateClientInfoService
  );

  // Эффект следит за состоянием запроса на обновление
  // профиля и вызывает соответствующий колбэк с задержкой,
  // чтобы успел посветиться индикатор успеха
  React.useEffect(() => {
    if (updateProfileState && updateProfileState.resolved) {
      return setCustomTimeout(() => {
        onSubmitDidSucceed();
      }, DONE_TIMEOUT_DURATION);
    }
  }, [onSubmitDidSucceed, updateProfileState, values]);

  // Хэндлер сабмита формы, валидирует форму и в
  // случае успеха отправляет запрос на обновление
  // данных профиля новыми значениями
  const onSubmit = React.useCallback(() => {
    validate(() => {
      updateProfile({ clientId, clientProfileFormValues: values });
    });
  }, [validate, updateProfile, clientId, values]);

  // Актуальный экран
  const screen = useAsyncActionViewScreen(updateProfileState);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {intl.clientDetails.profileEditor.title}
          </TextComponent>
        }
        actions={null}
        navigation={null}
      />
      <FormControlComponent onSubmit={onSubmit}>
        <SidebarModalSectionComponent
          content={
            <CustomFieldsetComponent disabled={screen !== 'ACTIVE'}>
              <ClientProfileInfoFormFieldset
                onChange={onChange}
                onLocationChange={onLocationChange}
                intlDictionary={intl}
                values={values}
                validation={validation}
              />
            </CustomFieldsetComponent>
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {intl.saveChangesButton}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={onCancel}
              >
                {intl.cancelButton}
              </ModalPressControlComponent>
            </>
          }
          navigation={null}
        />
      </FormControlComponent>
    </>
  );
};
